// Timeline runtime — interpolates a Timeline at a given time `t` (ms).
//
// Used in two places:
//   1. The editor's preview <Player> (DOM-applied via setAttribute / style).
//   2. The exporter (the same applied state, captured frame-by-frame onto a canvas).

import type { Keyframe, Timeline, Track } from "./ir";
import { easeFn } from "./easing";

export interface ResolvedFrame {
  opacity: number;
  scale: number;
  scaleX?: number;
  scaleY?: number;
  x: number;
  y: number;
  rotate: number;
  skewX?: number;
  fillOpacity?: number;
  strokeOpacity?: number;
  strokeDasharray?: number;
  strokeDashoffset?: number;
  fill?: string;
  stroke?: string;
  filter?: string;
}

const NUMERIC_KEYS = [
  "opacity", "scale", "scaleX", "scaleY", "x", "y", "rotate", "skewX",
  "fillOpacity", "strokeOpacity", "strokeDasharray", "strokeDashoffset",
] as const;

const STRING_KEYS = ["fill", "stroke"] as const;

// Parses `blur(12px)`, `brightness(1.2)`, `drop-shadow(0 8px 24px rgba(...))`
// for numeric interpolation. Anything more exotic (multi-arg drop-shadow with
// colors) we still interpolate by *number* tokens, leaving units and colors
// alone — perceptually identical to a smooth crossfade for the cases we use.
const FILTER_FN = /([\w-]+)\(([^)]*)\)/g;
const NUM_TOKEN = /-?\d*\.?\d+/g;

function interpolateNumberInString(a: string, b: string, t: number): string {
  // Walk both strings token-aligned. If one has more numbers, fall back to the
  // one with more so the animation still has "an end state".
  const aNums = [...a.matchAll(NUM_TOKEN)].map((m) => parseFloat(m[0]));
  const bNums = [...b.matchAll(NUM_TOKEN)].map((m) => parseFloat(m[0]));
  if (aNums.length === 0 && bNums.length === 0) return t < 0.5 ? a : b;
  // Build an interpolated number list, falling back element-wise.
  const out = aNums.map((av, i) => {
    const bv = bNums[i] ?? av;
    return av + (bv - av) * t;
  });
  // Walk the template (a) and replace numbers in order.
  let i = 0;
  return a.replace(NUM_TOKEN, () => {
    const n = out[i++] ?? 0;
    // Preserve at most 4 decimals — keeps the inline style strings sane.
    return Number.isInteger(n) ? String(n) : n.toFixed(4).replace(/\.?0+$/, "");
  });
}

export function interpolateFilter(a: string | undefined, b: string | undefined, t: number): string | undefined {
  if (!a && !b) return undefined;
  if (!a) return b;
  if (!b) return a;
  // If shapes differ entirely (different function ordering), snap at midpoint.
  const aFns = [...a.matchAll(FILTER_FN)].map((m) => m[1]);
  const bFns = [...b.matchAll(FILTER_FN)].map((m) => m[1]);
  if (aFns.length !== bFns.length || aFns.some((f, i) => f !== bFns[i])) {
    return t < 0.5 ? a : b;
  }
  return interpolateNumberInString(a, b, t);
}

export function resolveTrack(track: Track, tNorm: number): ResolvedFrame {
  const ease = easeFn(track.easing);
  const kf = track.keyframes;
  if (kf.length === 0) {
    return { opacity: 1, scale: 1, x: 0, y: 0, rotate: 0 };
  }
  // Find surrounding keyframes.
  let a = kf[0];
  let b = kf[kf.length - 1];
  for (let i = 0; i < kf.length - 1; i++) {
    if (tNorm >= kf[i].t && tNorm <= kf[i + 1].t) {
      a = kf[i]; b = kf[i + 1]; break;
    }
  }
  if (tNorm <= a.t) { a = kf[0]; b = kf[0]; }
  if (tNorm >= b.t) { a = kf[kf.length - 1]; b = kf[kf.length - 1]; }
  const span = Math.max(1e-6, b.t - a.t);
  const localT = ease(Math.min(1, Math.max(0, (tNorm - a.t) / span)));

  const out: ResolvedFrame = { opacity: 1, scale: 1, x: 0, y: 0, rotate: 0 };
  for (const k of NUMERIC_KEYS) {
    const av = (a as any)[k];
    const bv = (b as any)[k];
    if (av == null && bv == null) continue;
    const aa = av ?? bv ?? defaultFor(k);
    const bb = bv ?? av ?? defaultFor(k);
    (out as any)[k] = aa + (bb - aa) * localT;
  }
  for (const k of STRING_KEYS) {
    const av = (a as any)[k];
    const bv = (b as any)[k];
    (out as any)[k] = bv ?? av;
  }
  // Filter is special: interpolate the numbers inside the function-call list
  // so blur(12px) → blur(0px) crossfades smoothly instead of snapping.
  out.filter = interpolateFilter((a as any).filter, (b as any).filter, localT);
  return out;
}

function defaultFor(k: typeof NUMERIC_KEYS[number]): number {
  if (k === "opacity" || k === "scale" || k === "fillOpacity" || k === "strokeOpacity") return 1;
  return 0;
}

export interface AppliedTransform {
  selector: string;
  transform: string;
  opacity: number;
  attrs: Record<string, string>;
  styles: Record<string, string>;
}

export function applyAt(timeline: Timeline, ms: number): AppliedTransform[] {
  const tNorm = Math.min(1, Math.max(0, ms / Math.max(1, timeline.durationMs)));
  return timeline.tracks.map((tr) => trackToCss(tr, tNorm));
}

function trackToCss(track: Track, tNorm: number): AppliedTransform {
  const f = resolveTrack(track, tNorm);
  const sx = f.scaleX ?? f.scale ?? 1;
  const sy = f.scaleY ?? f.scale ?? 1;
  const transform = `translate(${f.x.toFixed(3)}px, ${f.y.toFixed(3)}px) scale(${sx}, ${sy}) rotate(${f.rotate}deg)${
    f.skewX ? ` skewX(${f.skewX}deg)` : ""
  }`;
  const attrs: Record<string, string> = {};
  const styles: Record<string, string> = {};
  if (f.fillOpacity != null)   attrs["fill-opacity"]    = f.fillOpacity.toFixed(4);
  if (f.strokeOpacity != null) attrs["stroke-opacity"]  = f.strokeOpacity.toFixed(4);
  if (f.strokeDasharray != null && f.strokeDashoffset != null) {
    // We use percentage-style dasharray — the runtime stamps each path with its
    // measured length and we feed strokeDashoffset = dashOffset * length.
    attrs["stroke-dasharray-norm"]  = f.strokeDasharray.toFixed(4);
    attrs["stroke-dashoffset-norm"] = f.strokeDashoffset.toFixed(4);
  }
  if (f.fill)   styles.fill   = f.fill;
  if (f.stroke) styles.stroke = f.stroke;
  if (f.filter) styles.filter = f.filter;
  return { selector: track.target, transform, opacity: f.opacity, attrs, styles };
}
